import { initApp, boot } from "./app";
import { initShell } from "./shell";
import { initVoiceMemo } from "./voicememo";
import { initContacts } from "./contacts";
import { initFaq } from "./faq";
import { initTasks } from "./tasks";
import { initMinutes } from "./minutes";
import { initDocs } from "./docs";
import { initQuotes } from "./quotes";
import { initReceipts } from "./receipts";
import { initVoiceForms } from "./voiceforms";
import { initConnect } from "./connect";
import { initHomeSearch, prefetchData } from "./homesearch";

// Installability (Add to Home Screen). The service worker is intentionally online-only.
if ("serviceWorker" in navigator) {
  navigator.serviceWorker.register("/sw.js").catch(() => {});
}
// Best-effort persistent storage so the linked session survives relaunches
// (esp. iOS, where PWA storage can be evicted → the "Let's reconnect" path).
void navigator.storage?.persist?.().catch?.(() => {});

// App-like PWA: block pinch-zoom in in-browser Safari too (standalone already honours
// user-scalable=no; touch-action kills double-tap zoom). A zoomed shell pans left-right
// and feels broken — Gery.
document.addEventListener("gesturestart", (e) => e.preventDefault());
// iOS keyboard-focus may still nudge the document viewport (the top bar slid off
// while scrolling in Connect) — pin it straight back, always.
const pinViewport = (): void => {
  if (window.scrollX || window.scrollY) window.scrollTo(0, 0);
};
window.addEventListener("scroll", pinViewport, { passive: true });

// When the iOS keyboard opens the visual viewport shrinks (vvH 458 vs 844 here — visualViewport DOES
// work). The shell (height:100vh + overflow:hidden) doesn't shrink, so a bottom-docked field hides
// behind the keyboard. And `body { height: var(--vvh) }` was overridden to 100vh by a later rule —
// so we set the body height INLINE (inline beats any stylesheet rule) to actually shrink the shell.
// fullH = the tallest visual viewport seen = the no-keyboard height (robust vs innerHeight quirks).
const vv = window.visualViewport;
let fullH = vv?.height || window.innerHeight;
const syncVV = (): void => {
  if (!vv) return;
  if (vv.height > fullH) fullH = vv.height;
  const kb = Math.max(0, Math.round(fullH - vv.height));
  const root = document.documentElement.style;
  if (kb > 80) {
    document.body.style.height = `${Math.round(vv.height)}px`; // inline → wins over the stylesheet
    root.setProperty("--vvh", `${Math.round(vv.height)}px`);   // feeds the fixed .cd editor sheets
    root.setProperty("--kb", `${kb}px`);                       // feeds the .rp-sheet bottom-sheets
  } else {
    document.body.style.height = "";
    root.removeProperty("--vvh");
    root.removeProperty("--kb");
  }
};
syncVV();
vv?.addEventListener("resize", () => { syncVV(); pinViewport(); });
vv?.addEventListener("scroll", () => { syncVV(); pinViewport(); });

// Theme — mint redesign system, dual light/dark. Keyed off [data-theme] on <html>.
const saved = localStorage.getItem("rplay-theme");
const theme = saved ?? (window.matchMedia("(prefers-color-scheme: light)").matches ? "light" : "dark");
document.documentElement.setAttribute("data-theme", theme);
document.body.classList.toggle("theme-light-bg", theme === "light");

initApp();
initShell();
initVoiceMemo();
initContacts();
initFaq();
initTasks();
initMinutes();
initDocs();
initQuotes();
initReceipts();
initVoiceForms();
initConnect();
initHomeSearch();
// Boot paints Home from the cached session; once the probe settles, warm every module
// (badges + cross-module search) — desktop-PWA prefetch parity, gated on a linked session.
void boot().then(() => prefetchData());
